import React from 'react';
import { useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { SidebarTab, setActiveSidebarTab } from '../../redux/slices/uiSlice';
import { useAppSelector } from '../../redux/store';
import { getLanguageDirection } from '../../i18n/config';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../ui/tooltip';

const SidebarTabs: React.FC = () => {
  const { t, i18n } = useTranslation();
  const dispatch = useDispatch();
  const isRTL = getLanguageDirection(i18n.language) === 'rtl';
  const activeSidebarTab = useAppSelector(state => state.UIReducer.activeSidebarTab);

  const handleTabChange = (value: SidebarTab) => {
    dispatch(setActiveSidebarTab(value));
  };

  const getTabText = (tab: SidebarTab) => {
    const translationKey = `components.sidebar.tabs.${tab}`;
    const translatedText = t(translationKey);

    if (translatedText === translationKey) {
      return tab.charAt(0).toUpperCase() + tab.slice(1);
    }

    return translatedText;
  };

  const getResponsiveFontSize = (text: string, allTexts: string[]) => {
    const maxLength = Math.max(...allTexts.map(t => t.length));
    const avgLength = allTexts.reduce((sum, t) => sum + t.length, 0) / allTexts.length;

    if (maxLength > 15 || avgLength > 12) return 'text-[0.7rem]';
    if (maxLength > 12 || avgLength > 10) return 'text-[0.75rem]';
    if (text.length > 8) return 'text-[0.8rem]';
    return 'text-sm';
  };

  const tabs: SidebarTab[] = ['cases', 'chats'];
  const allTabTexts = tabs.map(tab => getTabText(tab));

  return (
    <div className="px-4 pt-6" style={{ paddingTop: '25px' }}>
      <div className="flex bg-secondary rounded-xl p-1 gap-1 border border-border">
        {tabs.map((tab) => {
          const tabText = getTabText(tab);
          const fontSize = getResponsiveFontSize(tabText, allTabTexts);
          const isActive = activeSidebarTab === tab;
          const needsMultiLine = tabText.length > 12;

          return (
            <TooltipProvider key={tab}>
              <Tooltip>
                <TooltipTrigger asChild>
                  <button
                    onClick={() => handleTabChange(tab)}
                    className={`
                      flex-1 min-w-[60px] px-1 rounded-[6px] transition-smooth
                      ${needsMultiLine ? 'py-2' : 'py-3'}
                      ${isActive
                        ? 'bg-background shadow-sm font-bold text-foreground ring-1 ring-border'
                        : 'text-muted-foreground hover:text-accent-foreground'
                      }
                    `}
                  >
                    <span className={`${fontSize} ${needsMultiLine ? 'line-clamp-2' : 'truncate'} block px-1`}>
                      {tabText}
                    </span>
                  </button>
                </TooltipTrigger>
                {tabText.length > 8 && (
                  <TooltipContent>
                    <p>{tabText}</p>
                  </TooltipContent>
                )}
              </Tooltip>
            </TooltipProvider>
          );
        })}
      </div>
    </div>
  );
};

export default SidebarTabs;

